import { CircleHelp } from 'lucide-react';
import * as React from 'react';

import { Label } from '@/componentes/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/componentes/ui/tooltip';
import { cn } from '@/lib/utils';

/* Campo — a moldura de um controle de formulário: rótulo, ajuda e erro.
 *
 * O shadcn entrega Input e Label soltos; a amarração acessível entre eles
 * (id, aria-describedby, aria-invalid, aria-required) é sempre a mesma e
 * sempre esquecida. Aqui ela é feita UMA vez e entregue ao controle por
 * children-como-função, o que deixa explícito quais atributos precisam chegar
 * no <input>.
 *
 *   <Campo rotulo="Ticket médio" erro={erro}>
 *     {(a) => <Input {...a} value={v} onChange={...} />}
 *   </Campo>
 *
 * ── AJUDA E ERRO FICAM EMBAIXO, E OS DOIS NUNCA APARECEM JUNTOS ───────────
 * Colocar a ajuda ACIMA do controle foi tentado e desfeito, e vale registrar
 * por quê: a leitura melhora (regra antes de responder) mas o formulário
 * quebra. Neste produto os campos vivem em grade de duas colunas, e um campo
 * com ajuda passa a ser mais alto que o vizinho sem ajuda — os dois controles
 * da mesma linha deixam de começar na mesma altura e a grade inteira fica
 * torta. Embaixo, todos os rótulos têm a mesma altura e todos os controles se
 * alinham; a diferença de altura sobra para o fim da linha, onde ninguém a vê.
 *
 * Com erro na tela a ajuda sai: duas linhas de texto miúdo em volta de um
 * campo é ruído, e a que importa naquele instante é a que diz o que corrigir.
 * A ajuda é --tinta-2 (6,8:1) e não --tinta-3: em 12px, 4,8:1 é o piso da
 * norma, não uma leitura confortável.
 *
 * ── AJUDA E DICA NÃO SÃO A MESMA COISA ────────────────────────────────────
 * `ajuda` é a REGRA do campo, curta, que a pessoa precisa ler antes de
 * digitar ("Padrão de 12% quando não informada"). Fica visível, embaixo.
 *
 * `dica` é o CONCEITO por trás do campo: dois períodos que explicam o que o
 * número significa no negócio. Vive numa tooltip no rótulo porque quem já
 * sabe não precisa relê-la a cada visita, e impressa acima do controle ela
 * empurrava o formulário inteiro para baixo, transformando uma etapa de
 * cinco campos em duas dobras de texto.
 *
 * O gatilho é um BOTÃO de verdade, com aria-label: tooltip pendurada em
 * ícone decorativo não abre por teclado, e o conteúdo some para quem navega
 * sem mouse. */

export type AtributosControle = {
  id: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-required'?: boolean | undefined;
};

export type CampoProps = {
  rotulo?: string | undefined;
  /** A regra do campo. Some quando há erro. */
  ajuda?: string | undefined;
  /** O conceito por trás do campo, numa tooltip no rótulo. Ver o cabeçalho. */
  dica?: React.ReactNode;
  erro?: string | undefined;
  obrigatorio?: boolean | undefined;
  /** Ocupa a linha inteira do grid do formulário. */
  className?: string | undefined;
  id?: string | undefined;
  children: (atributos: AtributosControle) => React.ReactNode;
};

export function Campo({
  rotulo,
  ajuda,
  dica,
  erro,
  obrigatorio,
  className,
  id: idExterno,
  children,
}: CampoProps) {
  const idAuto = React.useId();
  const id = idExterno ?? idAuto;
  const idAjuda = `${id}-ajuda`;
  const idErro = `${id}-erro`;

  const descrito = erro ? idErro : ajuda ? idAjuda : undefined;

  return (
    <div className={cn('grid content-start gap-2', className)}>
      {rotulo ? (
        <div className="flex items-center gap-1.5">
          <Label htmlFor={id}>
            {rotulo}
            {/* O asterisco continua sendo só o sinal VISUAL, e continua
              aria-hidden: quem diz ao leitor de tela que o campo é
              obrigatório é o aria-required no controle, logo abaixo. Antes
              não era dito para ninguém. */}
            {obrigatorio ? (
              <span className="text-negativo" aria-hidden="true" title="Campo obrigatório">
                *
              </span>
            ) : null}
          </Label>

          {dica ? (
            <Tooltip>
              <TooltipTrigger
                type="button"
                aria-label={`O que é ${rotulo}`}
                /* Sem estilo de foco próprio: o anel do sistema já vem da
                   regra base de :focus-visible em globals.css. */
                className="text-tinta-3 hover:text-tinta-2 rounded-pilula transition-colors"
              >
                <CircleHelp className="size-4" />
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={6}>
                {dica}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      ) : null}

      {children({
        id,
        'aria-describedby': descrito,
        'aria-invalid': erro ? true : undefined,
        'aria-required': obrigatorio ? true : undefined,
      })}

      {erro ? (
        <p id={idErro} className="text-negativo text-nota font-medium" role="alert">
          {erro}
        </p>
      ) : ajuda ? (
        <p id={idAjuda} className="text-tinta-2 text-nota">
          {ajuda}
        </p>
      ) : null}
    </div>
  );
}
